import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { trackEmailOptIn } from "@/lib/posthog";

interface EmailCaptureProps {
  className?: string;
  variant?: "default" | "compact";
  source?: string;
  showDescription?: boolean;
}

export function EmailCapture({ className, variant = "default", source = "website", showDescription = true }: EmailCaptureProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  // The form can appear more than once on a page, so the input id is generated per instance.
  const inputId = useId();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    // zod and the Supabase client load when the form is used, not with the page. This form lives in
    // HomePage, which ships in the main bundle that every route downloads, and the client alone is
    // about 44 KB gzipped. Validation runs before the input is disabled, so a rejected address keeps
    // focus in the field and never fetches the Supabase chunk. A chunk that fails to load usually
    // means the site was republished since this tab opened, hence the refresh hint.
    let z: typeof import("zod")["z"];
    try {
      ({ z } = await import("zod"));
    } catch {
      toast.error("Something went wrong. Please refresh the page and try again.");
      return;
    }

    // Client-side email validation
    const result = z.string().email("Please enter a valid email").max(255).safeParse(email);
    if (!result.success) {
      toast.error(result.error.errors[0].message);
      return;
    }

    setStatus("loading");

    let supabase: typeof import("@/integrations/supabase/client")["supabase"];
    try {
      ({ supabase } = await import("@/integrations/supabase/client"));
    } catch {
      toast.error("Something went wrong. Please refresh the page and try again.");
      setStatus("idle");
      return;
    }

    // Submit via edge function (server-side rate limiting + validation)
    const { data, error: fnError } = await supabase.functions.invoke("submit-newsletter", {
      body: { email: email.trim().toLowerCase(), source },
    });

    if (fnError || (!data?.success && !data?.alreadySubscribed)) {
      const message = data?.error || "Something went wrong. Please try again.";
      toast.error(message);
      setStatus("idle");
      return;
    }

    if (data?.alreadySubscribed) {
      toast.info("You're already subscribed! Thank you for your interest.");
      setStatus("idle");
      return;
    }

    setStatus("success");
    toast.success("Thanks for joining the community!");

    // Track successful email opt-in
    trackEmailOptIn(source);

    // Reset after showing success
    setTimeout(() => {
      setEmail("");
      setStatus("idle");
    }, 3000);
  };

  if (status === "success") {
    return (
      <div className={cn("text-center py-8", className)}>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/20 mb-4 animate-scale-in">
          <Check className="w-8 h-8 text-secondary" />
        </div>
        <p className="text-lg text-foreground font-medium">Thank you!</p>
        <p className="text-muted-foreground mt-1">Thanks for joining the community.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "w-full",
        variant === "default" && "max-w-md mx-auto",
        className
      )}
    >
      <div className={cn(
        "flex gap-3 w-full",
        variant === "default" ? "flex-col" : "flex-col sm:flex-row"
      )}>
        <Input
          id={inputId}
          name="email"
          autoComplete="email"
          aria-label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="flex-1 bg-muted border-border focus:border-gold w-full"
          disabled={status === "loading"}
        />
        <Button
          type="submit"
          variant="gold"
          disabled={status === "loading"}
          className="w-full sm:w-auto shrink-0"
        >
          {status === "loading" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Joining...
            </>
          ) : (
          "Join the Journey"
        )}
      </Button>
    </div>
    {showDescription && (
      <p className="text-xs text-muted-foreground mt-3 text-center">
        Receive updates, special promotions, and exclusive content
      </p>
    )}
  </form>
  );
}